/**
 * Standalone slide-deck presenter route — mounted at
 * `/teacher/subjects/:subjectId/slides/:deckId`. Fixes Ninis observation #2:
 * clicking a slide-deck row on the Subject dashboard used to route to a URL
 * with no handler, so nothing opened. Now the same fullscreen `SlidesPresenter`
 * component used from inside a chapter opens directly.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { SlidesPresenter } from "@/components/slides/SlidesBody";

export default function SlideDeckView() {
  const { subjectId, deckId } = useParams();
  const nav = useNavigate();
  const [deck, setDeck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/ai/slides/${deckId}`);
        setDeck(data.deck);
      } catch (e) {
        const status = e.response?.status;
        setNotFound(true);
        if (status === 404) toast.error("Slide deck not found");
        else toast.error(e.response?.data?.detail || "Could not load slide deck");
      } finally {
        setLoading(false);
      }
    })();
  }, [deckId]);

  const goBack = () => nav(subjectId ? `/teacher/subjects/${subjectId}` : "/teacher/subjects");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center gap-3 text-neutral-500">
        <Loader2 className="animate-spin" size={18} /> Loading slide deck…
      </div>
    );
  }
  if (notFound || !deck) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-3">
        <div className="text-neutral-500">Slide deck not found or you don&apos;t have access.</div>
        <button
          onClick={goBack}
          data-testid="slide-deck-back"
          className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-[#0A0C10] text-white hover:bg-black"
        >
          <ArrowLeft size={14} /> Back to subject
        </button>
      </div>
    );
  }
  return (
    <SlidesPresenter
      deck={deck}
      onClose={goBack}
      onUpdated={(d) => setDeck(d)}
    />
  );
}
